import { useCallback, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { preloadRoute } from '@/routes/routeChunks'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import { LogOut, ChevronDown, ChevronLeft, ChevronRight, Key, Menu, Building2, ShieldCheck } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getInitials } from '@/lib/utils'
import { isSuperAdmin } from '@/lib/role'
import { ChangePasswordDialog } from './ChangePasswordDialog'
import { MfaSettingsDialog } from './MfaSettingsDialog'
import { SwitchCompanyDialog } from './SwitchCompanyDialog'
import { useModuleConfig } from '@/hooks/useModuleEnabled'
import { useMyTenants } from '@/hooks/useMyTenants'
import { navItems, superAdminNavItems, visibleNavChildren, type NavItem } from './navItems'
import { navChildIsActive, navItemIsActive } from './sidebarNavActive'
import type { User } from '@/types'

// Navigation list — extracted from SidebarContent to keep that component readable.
function SidebarNav({
  items,
  prefix,
  collapsed,
  onNavigate,
  user,
}: {
  items: NavItem[]
  prefix: string
  collapsed?: boolean
  onNavigate?: () => void
  user: User | null
}) {
  const location = useLocation()

  const isActive = (href: string, children?: { href: string }[]) =>
    navItemIsActive({
      href,
      children,
      prefix,
      pathname: location.pathname,
      search: location.search,
    })

  const isChildActive = (childHref: string, siblings: { href: string }[]) =>
    navChildIsActive({
      childHref,
      siblings,
      prefix,
      pathname: location.pathname,
      search: location.search,
    })

  // One delegated listener beats a handler on each of the five Link sites
  // below: mouseover and focusin both bubble to this nav. It starts the target
  // route's chunk while the pointer is still travelling, so the click usually
  // finds the module already loaded.
  const preloadOnHover = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      const href = (event.target as HTMLElement).closest('a')?.getAttribute('href')
      if (!href) return
      const path = prefix && href.startsWith(prefix) ? href.slice(prefix.length) : href
      preloadRoute(path, Boolean(prefix))
    },
    [prefix],
  )

  return (
    <nav
      className={cn('flex-1 space-y-1 overflow-y-auto whitespace-nowrap', collapsed ? 'p-2' : 'p-4')}
      onMouseOver={preloadOnHover}
      onFocus={preloadOnHover}
    >
      {items.map((item) => {
        const Icon = item.icon

        // Section headers — non-clickable labels; hide when collapsed
        if (item.isSection) {
          if (collapsed) return null
          return (
            <p
              key={item.href}
              className="mt-5 mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {item.label}
            </p>
          )
        }

        const active = isActive(item.href, item.children)

        if (item.children) {
          const children = item.children
          if (collapsed) {
            // Icon-only with tooltip showing parent label
            return (
              <Tooltip key={item.href}>
                <TooltipTrigger asChild>
                  <Link
                    to={`${prefix}${item.href}`}
                    onClick={onNavigate}
                    className={cn(
                      'flex items-center justify-center rounded-lg p-2 transition-colors',
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="size-4.5 shrink-0" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            )
          }

          return (
            <div key={item.href} className="space-y-1">
              <Link
                to={`${prefix}${item.href}`}
                onClick={onNavigate}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary/10 text-primary font-semibold border-l-[3px] border-primary'
                    : 'border-l-[3px] border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="size-4.5 shrink-0" />
                {item.label}
              </Link>
              <div className="ml-[30px] space-y-0.5">
                {visibleNavChildren(children, {
                  moduleConfig: user?.module_config,
                }).map((child) => (
                  <Link
                    key={child.href}
                    to={`${prefix}${child.href}`}
                    onClick={onNavigate}
                    className={cn(
                      'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                      isChildActive(child.href, children)
                        ? 'text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            </div>
          )
        }

        if (collapsed) {
          return (
            <Tooltip key={item.href}>
              <TooltipTrigger asChild>
                <Link
                  to={`${prefix}${item.href}`}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center justify-center rounded-lg p-2 transition-colors',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <Icon className="size-4.5 shrink-0" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          )
        }

        return (
          <Link
            key={item.href}
            to={`${prefix}${item.href}`}
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              active
                ? 'bg-primary/10 text-primary font-semibold border-l-[3px] border-primary'
                : 'border-l-[3px] border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Icon className="size-4.5 shrink-0" />
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

// User menu footer — extracted from SidebarContent to keep that component readable.
function SidebarUserMenu({
  collapsed,
  displayName,
  user,
  canSwitchCompany,
  onLogout,
  onChangePassword,
  onMfaSettings,
  onSwitchCompany,
}: {
  collapsed?: boolean
  displayName: string
  user: User | null
  canSwitchCompany: boolean
  onLogout: () => void
  onChangePassword: () => void
  onMfaSettings: () => void
  onSwitchCompany: () => void
}) {
  return (
    <div className={cn('shrink-0 border-t', collapsed ? 'p-2' : 'p-4')}>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="w-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {getInitials(displayName)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={onChangePassword}>
                  <Key className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onMfaSettings}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Two-Factor Auth
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout} variant="destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent side="right">
            {displayName}
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-3 px-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  {user ? getInitials(`${user.first_name} ${user.last_name}`) : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">
                  {displayName}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role.replace('_', ' ')}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {canSwitchCompany && (
              <DropdownMenuItem onClick={onSwitchCompany}>
                <Building2 className="mr-2 h-4 w-4" />
                Switch Company
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onChangePassword}>
              <Key className="mr-2 h-4 w-4" />
              Change Password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMfaSettings}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Two-Factor Auth
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLogout} variant="destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

function SidebarContent({
  onNavigate,
  collapsed,
}: {
  onNavigate?: () => void
  collapsed?: boolean
}) {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const tenants = useAuthStore((s) => s.tenants)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const logout = useAuthStore((s) => s.logout)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [mfaSettingsOpen, setMfaSettingsOpen] = useState(false)
  const [switchCompanyOpen, setSwitchCompanyOpen] = useState(false)
  // Members assigned to more than one tenant can switch between them.
  const canSwitchCompany = !isSuperAdmin(user?.role) && tenants.length > 1
  // Never render a blank name: fall back to email when first/last are empty
  // (e.g. a user provisioned via an invitation that captured no name).
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
    user?.email ||
    'User'

  // Sync module config from server (auto-refreshes on window focus)
  useModuleConfig()
  // Keep the switchable-companies list fresh for multi-tenant members.
  useMyTenants()

  const filteredNavItems = (() => {
    if (isSuperAdmin(user?.role) && !activeTenantId) {
      return superAdminNavItems
    }
    const items = navItems.filter((item) => {
      if (item.module && !user?.module_config?.[item.module]) return false
      if (!item.roles) return true
      if (isSuperAdmin(user?.role) && activeTenantId) return true
      return user && item.roles.includes(user.role)
    })
    // Multi-brand members get a brand comparison entry; a single-brand member
    // would only see a pointless 1-row "comparison", so hide it for them.
    if (canSwitchCompany) {
      items.push({ label: 'Brand Comparison', href: '/brand-comparison', icon: Building2 })
    }
    return items
  })()

  const prefix = activeTenantId
    ? `/${activeTenantId}`
    : !isSuperAdmin(user?.role) && tenant?.id
      ? `/${tenant.id}`
      : ''

  return (
    <TooltipProvider delayDuration={0}>
      <aside className="flex h-full flex-col bg-card overflow-hidden">
        {/* Logo */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center border-b',
            collapsed ? 'justify-center px-0' : 'px-4'
          )}
        >
          {collapsed ? (
            <img src="/favicon.png" alt="FDC" className="h-8 w-8 object-contain" />
          ) : (
            <img src="/fdclogo-banner.png" alt="FrontDeskCollect.ai" className="h-8 w-auto" />
          )}
        </div>

        <SidebarNav
          items={filteredNavItems}
          prefix={prefix}
          collapsed={collapsed}
          onNavigate={onNavigate}
          user={user}
        />

        {/* User menu */}
        <SidebarUserMenu
          collapsed={collapsed}
          displayName={displayName}
          user={user}
          canSwitchCompany={canSwitchCompany}
          onLogout={logout}
          onChangePassword={() => setChangePasswordOpen(true)}
          onMfaSettings={() => setMfaSettingsOpen(true)}
          onSwitchCompany={() => setSwitchCompanyOpen(true)}
        />

        <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
        <MfaSettingsDialog open={mfaSettingsOpen} onOpenChange={setMfaSettingsOpen} />
        <SwitchCompanyDialog open={switchCompanyOpen} onOpenChange={setSwitchCompanyOpen} />
      </aside>
    </TooltipProvider>
  )
}

export function MobileSidebar() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  // Close drawer on route change — adjusted during render via a prev-prop guard
  // instead of an effect, so the drawer never briefly persists on the new route.
  const [prevPathname, setPrevPathname] = useState(pathname)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname)
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64 p-0 overflow-y-auto" aria-describedby={undefined}>
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <SidebarContent onNavigate={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  )
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebar-collapsed:v1') === 'true'
    } catch {
      // localStorage unavailable (private browsing) — default to expanded
      return false
    }
  })

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem('sidebar-collapsed:v1', String(next))
      } catch {
        // best-effort persistence only
      }
      return next
    })
  }

  return (
    <div
      className={cn(
        'hidden md:flex h-screen border-r flex-col transition duration-200',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex-1 overflow-hidden">
        <SidebarContent collapsed={collapsed} />
      </div>

      {/* Collapse toggle button */}
      <div className={cn('shrink-0 border-t flex', collapsed ? 'justify-center p-2' : 'justify-end p-2')}>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          className="h-8 w-8 rounded-full"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  )
}
